/**
 * Scan a sheet that is coming back and record how many labels are still on it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import { fetchInventoryBox, fetchInventoryBoxPutawayContext, type InventoryBox } from "@/lib/inventoryBoxApi";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { parseWarehouseRecountScan } from "./warehouseRecountScan";
import {
  WarehouseRecountShelfField,
  warehouseRecountShelfDraftToStorageKey,
  warehouseRecountShelfStorageKeyToDraft,
} from "./WarehouseRecountShelfField";

const RETURN_HOVER_HINT =
  "Scan the sheet label and enter how many labels are still on it. "
  + "That sheet goes back on the shelf at that quantity. Labels that were used come off on-hand. "
  + "The sheet is not tied to an order.";

export function WarehouseReturnMaterialsPage(): JSX.Element {
  const { showToast } = useHubToast();
  const scanRef = useRef<HTMLInputElement | null>(null);
  const [scanDraft, setScanDraft] = useState("");
  const [sheet, setSheet] = useState<InventoryBox | null>(null);
  const [quantityDraft, setQuantityDraft] = useState("");
  const [shelfDraft, setShelfDraft] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const loadSheet = useCallback(async (raw: string) => {
    const parsed = parseWarehouseRecountScan(raw);
    if (parsed == null || parsed.kind !== "box") {
      setScanError("Scan the sheet label.");
      return;
    }
    setPending(true);
    setScanError(null);
    try {
      const box = await fetchInventoryBox(parsed.hex);
      setSheet(box);
      setQuantityDraft("");
      setShelfDraft(warehouseRecountShelfStorageKeyToDraft(box.sublocation));
      setScanDraft("");
    } catch (error) {
      setSheet(null);
      setScanError(error instanceof ApiError ? error.message : "That sheet was not found.");
    } finally {
      setPending(false);
    }
  }, []);

  useFloorPageScanCapture({
    inputRef: scanRef,
    autoFocus: sheet == null,
    onScan: (value) => {
      void loadSheet(value);
    },
  });

  const shelvesQuery = useQuery({
    queryKey: ["warehouse-return-shelves", sheet?.hex, sheet?.location_id],
    enabled: sheet != null && sheet.location_id != null && sheet.location_id > 0,
    queryFn: () => fetchInventoryBoxPutawayContext(sheet?.hex ?? "", sheet?.location_id ?? 0, "shelves"),
  });

  const quantityRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (sheet == null) return;
    quantityRef.current?.focus();
  }, [sheet]);
  const returnedQuantity = Number.parseInt(quantityDraft.trim(), 10);
  const quantityValid =
    sheet != null
    && Number.isInteger(returnedQuantity)
    && returnedQuantity > 0
    && returnedQuantity <= sheet.quantity;

  const submitReturn = async () => {
    if (sheet == null || !quantityValid || pending) return;
    setPending(true);
    setScanError(null);
    try {
      const result = await apiFetch<{ box: InventoryBox }>("/api/warehouse/return-materials", {
        method: "POST",
        body: {
          box_hex: sheet.hex,
          quantity: returnedQuantity,
          sublocation: warehouseRecountShelfDraftToStorageKey(shelfDraft),
        },
      });
      showToast({
        tone: "success",
        message: `Sheet ${result.box.short_code} is back with ${String(result.box.quantity)}.`,
      });
      setSheet(null);
      setQuantityDraft("");
      setShelfDraft("");
      scanRef.current?.focus();
    } catch (error) {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not return that sheet.",
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={RETURN_HOVER_HINT} breadcrumbLabel="Return materials">
        Return materials
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Scan sheet</CardTitle>
        </CardHeader>
        <CardBody>
          <Label htmlFor="warehouse-return-scan">Sheet label</Label>
          <Input
            id="warehouse-return-scan"
            ref={scanRef}
            className="mt-1 text-base"
            value={scanDraft}
            autoComplete="off"
            placeholder="Scan the sheet label"
            onChange={(event) => setScanDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              void loadSheet(scanDraft);
            }}
          />
          {scanError != null ? (
            <p role="alert" className="mt-2 flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4" />
              {scanError}
            </p>
          ) : null}
        </CardBody>
      </Card>

      {sheet != null ? (
        <Card>
          <CardHeader>
            <CardTitle>Quantity coming back</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void submitReturn();
              }}
            >
              <p className="font-mono text-lg font-semibold text-slate-900">{sheet.short_code}</p>
              <p className="font-mono text-sm text-slate-800">{sheet.sku}</p>
              {sheet.mv_description.trim() !== "" ? (
                <p className="text-sm text-slate-600">{sheet.mv_description}</p>
              ) : null}
              <p className="text-sm text-slate-700">
                {sheet.archived_at != null
                  ? `This sheet was picked with ${String(sheet.quantity)} on it.`
                  : `This sheet has ${String(sheet.quantity)} on it.`}
              </p>
              <WarehouseRecountShelfField
                id="warehouse-return-shelf"
                shelfDraft={shelfDraft}
                shelfSublocations={shelvesQuery.data?.shelf_sublocations ?? []}
                disabled={pending}
                onShelfDraftChange={setShelfDraft}
                onCommit={setShelfDraft}
              />
              <div>
                <Label htmlFor="warehouse-return-quantity">Quantity coming back</Label>
                <Input
                  id="warehouse-return-quantity"
                  ref={quantityRef}
                  className="mt-1 text-base"
                  inputMode="numeric"
                  autoComplete="off"
                  value={quantityDraft}
                  onChange={(event) => setQuantityDraft(event.target.value)}
                />
              </div>
              <Button type="submit" disabled={!quantityValid || pending}>
                Return to shelf
              </Button>
            </form>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
